import { ChatMessage } from '../types/chat';
import { WS_URL } from '../config/apiConfig';

type MessageHandler = (message: ChatMessage) => void;
type NotificationHandler = (notification: any) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private messageHandlers: MessageHandler[] = [];
  private notificationHandlers: NotificationHandler[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 2;
  private reconnectDelay = 5000;
  private intentionalDisconnect = false;
  private token: string | null = null;
  private connecting = false;
  private serviceChecked = false;
  private serviceAvailable = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Quick reachability probe. Uses a short timeout to avoid the long
   * TCP timeout that causes ERR_CONNECTION_TIMED_OUT in the console.
   * Checks if the secure-messaging service is available.
   */
  private async isServiceReachable(): Promise<boolean> {
    if (!this.token) return false;
    try {
      const baseUrl = window.location.protocol === 'https:' ? '' : `${window.location.protocol}//${window.location.hostname}:8080`;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000);
      const resp = await fetch(`${baseUrl}/api/v1/chat/threads`, {
        method: 'HEAD',
        signal: controller.signal,
        headers: { Authorization: `Bearer ${this.token}` },
      });
      clearTimeout(timeout);
      // 401 = reachable but token expired/invalid — do not open WS with bad token
      if (resp.status === 401 || resp.status === 403) return false;
      // 404 means the endpoint doesn't exist (service not deployed)
      return resp.status !== 404 && resp.status !== 502 && resp.status !== 503;
    } catch {
      return false;
    }
  }

  async connect(token: string) {
    if (!token) return;
    // Prevent concurrent connect calls (React StrictMode double-invoke)
    if (this.connecting) return;
    // Already connected with same token
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.token === token) return;
    // Service was already checked and found unavailable – skip silently
    if (this.serviceChecked && !this.serviceAvailable) return;

    // If token changed, allow re-probe
    if (this.token && this.token !== token) {
      this.serviceChecked = false;
      this.serviceAvailable = false;
      if (this.ws) {
        try { this.ws.close(); } catch { /* ignore */ }
        this.ws = null;
      }
    }

    this.token = token;
    this.intentionalDisconnect = false;
    this.connecting = true;

    // WebSocket connects through the API Gateway; HTTPS pages must use WSS.
    const wsBaseUrl = WS_URL || `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;

    // Pre-flight: check if messaging service is reachable (only once)
    if (!this.serviceChecked) {
      const reachable = await this.isServiceReachable();
      this.serviceChecked = true;
      this.serviceAvailable = reachable;
      if (!reachable) {
        // Silent log - this is expected if messaging service is not running
        console.debug(
          'WebSocket: messaging service not reachable – chat/realtime disabled.'
        );
        this.connecting = false;
        return;
      }
    }

    // Bail if disconnect was called while we were probing
    if (this.intentionalDisconnect) {
      this.connecting = false;
      return;
    }

    const url = `${wsBaseUrl}/api/v1/chat/realtime?token=${token}`;

    try {
      this.ws = new WebSocket(url);
    } catch {
      console.debug('WebSocket: could not create connection');
      this.connecting = false;
      return;
    }

    // Hard timeout: if not open within 8s, close and stop retrying
    this.connectTimeout = setTimeout(() => {
      if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
        this.serviceAvailable = false;
        this.ws.close();
      }
    }, 8000);

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.connecting = false;
      this.serviceAvailable = true;
      if (this.connectTimeout) { clearTimeout(this.connectTimeout); this.connectTimeout = null; }
      console.debug('WebSocket connected to messaging service');
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'chat.message' || data.type === 'chat.message.urgent') {
          this.messageHandlers.forEach(h => h(data.data));
        } else if (data.type === 'notification') {
          this.notificationHandlers.forEach(h => h(data.data));
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    this.ws.onerror = () => {
      this.connecting = false;
    };

    this.ws.onclose = () => {
      this.connecting = false;
      if (this.connectTimeout) { clearTimeout(this.connectTimeout); this.connectTimeout = null; }
      if (!this.intentionalDisconnect && this.token && this.serviceAvailable) {
        this.attemptReconnect();
      }
    };
  }

  private attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      // Silent debug log instead of info
      console.debug('WebSocket: messaging service unavailable after %d attempts – chat disabled', this.maxReconnectAttempts);
      this.serviceAvailable = false;
      return;
    }
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(async () => {
      if (!this.token || this.intentionalDisconnect) return;
      // Re-check auth before reconnecting to avoid 401 spam
      const ok = await this.isServiceReachable();
      if (!ok) {
        this.serviceAvailable = false;
        return;
      }
      this.connect(this.token);
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  disconnect() {
    this.intentionalDisconnect = true;
    this.token = null;
    this.connecting = false;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    if (this.connectTimeout) { clearTimeout(this.connectTimeout); this.connectTimeout = null; }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.messageHandlers = [];
    this.notificationHandlers = [];
    this.reconnectAttempts = 0;
  }

  /** Allow re-checking service availability (e.g. after messaging service starts) */
  resetServiceCheck() {
    this.serviceChecked = false;
    this.serviceAvailable = false;
  }

  onMessage(handler: MessageHandler) { this.messageHandlers.push(handler); }
  onNotification(handler: NotificationHandler) { this.notificationHandlers.push(handler); }
  removeMessageHandler(handler: MessageHandler) { this.messageHandlers = this.messageHandlers.filter(h => h !== handler); }
  removeNotificationHandler(handler: NotificationHandler) { this.notificationHandlers = this.notificationHandlers.filter(h => h !== handler); }
}

export const websocketService = new WebSocketService();
